import { Temporal } from '@js-temporal/polyfill';

import { FLOYDIAN_FACTOR } from '@/lib/currency';
import type { IAvailability } from '@/types/availability-rules';
import type { ILOS, ILOSRecord, IPricing } from '@/types/pricing';

import { buildLOSForCheckInNight } from './bookable-night';
import type { BookableNights, NightlyLOS } from './types';
import {
	arrayOrEmptyIfAllZeroes,
	formatDate,
	getDatesInRange,
	getDayOfWeek,
	maxDate,
	minDate,
	toPlainDate,
} from './utils';

const MIN_ADVANCED_NOTICE_DAYS = 1;
const MAX_DAYS_TO_PRICE = 730;

const roundTwoDecimals = (value: number) => Math.round(value * 100) / 100;

/**
 * Builds the set of nights that are bookable (available and priced). Each night
 * carries its base price plus the price it would have under each of the
 * period's length-of-stay discounts, and whether a stay may start on it.
 */
const getBookableNights = (
	pricing: IPricing[],
	availability: IAvailability[],
	bookingFee: number,
	fxRate: number,
	minBookingNotice: number,
): BookableNights => {
	const dates: BookableNights = {};
	const today = Temporal.Now.plainDateISO();
	const firstToPrice = today.add({ days: minBookingNotice });
	const lastToPrice = today.add({ days: MAX_DAYS_TO_PRICE });

	// Expand the availability periods into the set of individual unblocked dates.
	// `end_on` is the day the guest leaves, so the last bookable night is the day before.
	const unblockedDates = new Set<string>();
	availability.forEach((period) => {
		const start = maxDate(toPlainDate(period.start_on), firstToPrice);
		const end = minDate(
			toPlainDate(period.end_on).subtract({ days: 1 }),
			lastToPrice,
		);
		getDatesInRange(start, end).forEach((date) =>
			unblockedDates.add(formatDate(date)),
		);
	});

	pricing.forEach((period, periodIndex) => {
		const startDate = toPlainDate(period.start_date);
		const endDate = toPlainDate(period.end_date);

		// Pricing periods entirely in the past have nothing to offer.
		if (Temporal.PlainDate.compare(endDate, today) <= 0) {
			return;
		}

		getDatesInRange(startDate, minDate(lastToPrice, endDate))
			.filter((date) => unblockedDates.has(formatDate(date)))
			.forEach((date) => {
				const dayOfWeek = getDayOfWeek(date);
				const weekOfPrices = period.prices.los[dayOfWeek];
				const weekOfAvailability = period.los_availability[dayOfWeek];

				// The number of leading `false` entries in los_availability is the minimum
				// stay. All seven `false` means no stay may start on this day.
				const firstAllowedIdx = weekOfAvailability.findIndex(
					(allowed) => allowed,
				);
				const canCheckIn = firstAllowedIdx !== -1;
				const minStay = firstAllowedIdx + 1;

				// The booking fee is applied per night so the compound LOS price carries it.
				const basePrice = roundTwoDecimals(
					(weekOfPrices[0] / FLOYDIAN_FACTOR) *
						(bookingFee + 1) *
						fxRate,
				);
				const discounts = period.discounts ?? [];

				dates[formatDate(date)] = {
					date,
					// Consumers rely on the base ('0') price being present; discounted prices are
					// keyed by the length of stay that unlocks them.
					price: discounts.reduce<Record<string, number>>(
						(prices, discount) => ({
							...prices,
							[discount.nights.toString()]:
								discount.factor * basePrice,
						}),
						{ '0': basePrice },
					),
					canCheckIn,
					minStay,
					period: periodIndex,
				};
			});
	});

	return dates;
};

/**
 * Compound length-of-stay prices for every night a stay may start on, walking
 * forward until the first unbookable night (or the 61-night maximum).
 */
const buildLOSForBookableStays = (
	bookableNights: BookableNights,
): NightlyLOS[] =>
	Object.entries(bookableNights)
		.filter(([, checkInNight]) => checkInNight.canCheckIn)
		.map(([checkInDateStr, checkInNight]) => ({
			checkInDateStr,
			checkInDate: checkInNight.date,
			los: arrayOrEmptyIfAllZeroes(
				buildLOSForCheckInNight(bookableNights, checkInNight),
			),
		}));

/**
 * Turns Travelnest availability and pricing into a length-of-stay (LOS) price
 * table: for each check-in date, the total price of a 1, 2, 3 ... night stay.
 * A zero means that stay length is not allowed from that date.
 *
 * @param advanceBookingDays - the property's minimum notice, in days
 * @param fxRate - multiplier applied to every price (1 for no conversion)
 * @param bookingFee - the guest booking fee as a rate, e.g. 0.1 for 10%
 */
export const buildLOS = (
	availability: IAvailability[],
	pricing: IPricing[],
	capacity: number,
	advanceBookingDays: number,
	fxRate: number,
	bookingFee: number,
): ILOS => {
	if (!pricing || pricing.length === 0) {
		return {};
	}

	// Never price today: nightly sync runs late in the day, so same-day stays are not safe.
	const minBookingNotice = Math.max(
		MIN_ADVANCED_NOTICE_DAYS,
		Number.isFinite(advanceBookingDays) ? advanceBookingDays : 0,
	);

	const bookableNights = getBookableNights(
		pricing,
		availability,
		bookingFee,
		fxRate,
		minBookingNotice,
	);
	const compoundNightlyPricing = buildLOSForBookableStays(bookableNights);

	const los: Record<string, ILOSRecord[]> = {};
	compoundNightlyPricing.forEach((night) => {
		if (night.los.length > 0) {
			los[night.checkInDateStr] = [
				{ guests: capacity, price: night.los },
			];
		}
	});

	return los;
};

export type StayPriceResult =
	{ stayPrice: number; minNights: number } | { error: string };

/**
 * Looks a stay up in the LOS table: the price for these dates and guests, or
 * the reason it cannot be booked.
 */
export const getStayPrice = (
	checkInDate: string,
	checkOutDate: string,
	guestCount: number,
	los: ILOS,
): StayPriceResult => {
	const checkIn = toPlainDate(checkInDate);
	const checkInLOS = los?.[formatDate(checkIn)];
	if (!checkInLOS) {
		return {
			error: 'There were no available stays for the requested check in date',
		};
	}

	const guestsLOS = checkInLOS.find((record) => guestCount <= record.guests);
	if (!guestsLOS) {
		return {
			error: 'There were no available stays for the requested number of guests',
		};
	}

	const prices = guestsLOS.price ?? [];
	const minNightsIdx = prices.findIndex((price) => price > 0);
	if (minNightsIdx === -1) {
		return {
			error: 'There were no available stays for the requested number of nights',
		};
	}

	const minNights = minNightsIdx + 1;
	const nights = checkIn.until(toPlainDate(checkOutDate)).days;
	if (minNights > nights) {
		return {
			error: `A stay starting on this date must be at least ${minNights} nights long`,
		};
	}

	const stayPrice = prices[nights - 1];
	if (!stayPrice) {
		return {
			error: 'There were no available stays for the requested number of nights',
		};
	}

	if (stayPrice <= 0) {
		return { error: 'The requested stay is not available' };
	}

	return { stayPrice, minNights };
};
